import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Wallet, TrendingUp, TrendingDown, Receipt, FileText, Plus, Trash2, CheckCircle2, AlertTriangle, Download, Copy, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';
import { CompanyContext } from '../contexts/CompanyContext';
import { generateInvoicePDF } from '../lib/pdf';
import { useT } from '../contexts/LanguageContext';
import BuildingFilter from '../components/BuildingFilter';

function StatCard({ label, value, sub, color = 'amber', Icon }) {
  const colors = {
    amber: 'from-amber-50 to-yellow-50 border-amber-200 text-amber-900',
    emerald: 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-900',
    rose: 'from-rose-50 to-red-50 border-rose-200 text-rose-900',
    slate: 'from-slate-50 to-slate-100 border-slate-200 text-slate-900',
  };
  return (
    <Card className={`p-4 bg-gradient-to-br ${colors[color]} border`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-1">{value}</div>
          {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
        </div>
        {Icon && <Icon className="w-6 h-6 opacity-40" />}
      </div>
    </Card>
  );
}

export default function Accounting() {
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const { company } = useContext(CompanyContext);
  const t = useT();
  const [tab, setTab] = useState('invoices');
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(null); // 'invoice' | 'bill' | 'payment'
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Building / date-range filters (Phase 18.8)
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [buildings, setBuildings] = useState([]);
  // Phase 24 — Advanced Invoice Payments
  const [invoiceSources, setInvoiceSources] = useState({ agreements: [], lpos: [] });
  const [sourceInfo, setSourceInfo] = useState(null); // {total, previously_invoiced, balance_remaining, prior_invoices}
  const [loadingSource, setLoadingSource] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (buildingFilter && buildingFilter !== 'all') qp.append('building_id', buildingFilter);
      if (dateFrom) qp.append('date_from', dateFrom);
      if (dateTo) qp.append('date_to', dateTo);
      const suffix = qp.toString() ? `?${qp.toString()}` : '';
      const [s, i, b, p] = await Promise.all([
        axios.get(`${API}/accounting/summary${suffix}`, { withCredentials: true }),
        axios.get(`${API}/invoices${suffix}`, { withCredentials: true }),
        axios.get(`${API}/bills${suffix}`, { withCredentials: true }),
        axios.get(`${API}/payments${suffix}`, { withCredentials: true }),
      ]);
      setSummary(s.data); setInvoices(i.data || []); setBills(b.data || []); setPayments(p.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load accounting data');
    }
    setLoading(false);
  };

  useEffect(() => {
    axios.get(`${API}/buildings`, { withCredentials: true })
      .then(r => setBuildings(r.data || []))
      .catch(() => setBuildings([]));
  }, []);

  useEffect(() => { fetchAll(); }, [buildingFilter, dateFrom, dateTo]); // eslint-disable-line

  const canWrite = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'ACCOUNTANT'].includes(user?.role);

  // Phase 24 — fetch agreements + LPOs list when opening invoice dialog
  const fetchInvoiceSources = async () => {
    try {
      const r = await axios.get(`${API}/invoices/sources`, { withCredentials: true });
      setInvoiceSources(r.data || { agreements: [], lpos: [] });
    } catch (e) {
      setInvoiceSources({ agreements: [], lpos: [] });
    }
  };

  // Phase 24 — fetch source-info (total / invoiced / balance) when source picked
  const fetchSourceInfo = async (source_type, source_id) => {
    if (!source_type || source_type === 'standalone' || !source_id) {
      setSourceInfo(null);
      return;
    }
    setLoadingSource(true);
    try {
      const r = await axios.get(`${API}/invoices/source-info`, {
        params: { source_type, source_id },
        withCredentials: true,
      });
      setSourceInfo(r.data);
    } catch (e) {
      setSourceInfo(null);
      toast.error(e.response?.data?.detail || 'Failed to load source info');
    }
    setLoadingSource(false);
  };

  const openInvoice = () => {
    setForm({
      issue_date: new Date().toISOString().slice(0, 10),
      due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      amount: 0,
      tax_amount: 0,
      source_type: 'standalone',
      payment_type: 'standalone',
    });
    setSourceInfo(null);
    fetchInvoiceSources();
    setOpen('invoice');
  };
  const openBill = () => { setForm({ issue_date: new Date().toISOString().slice(0, 10), due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10), amount: 0, tax_amount: 0 }); setOpen('bill'); };
  const openPayment = (pre = {}) => { setForm({ paid_at: new Date().toISOString().slice(0, 10), method: 'bank', amount: 0, ...pre }); setOpen('payment'); };

  // Phase 24 — when source_type / source_id changes, refresh source info & autofill linked fields
  const handleSourceTypeChange = (v) => {
    const nextForm = { ...form, source_type: v, source_id: null, source_ref: null, percentage: 0 };
    if (v === 'standalone') {
      nextForm.payment_type = 'standalone';
      nextForm.total_agreement_amount = 0;
    }
    setForm(nextForm);
    setSourceInfo(null);
  };

  const handleSourcePick = async (source_id) => {
    setForm({ ...form, source_id, percentage: 0, amount: 0 });
    await fetchSourceInfo(form.source_type, source_id);
  };

  // When user types a percentage, auto-compute amount (for advance/progressive)
  const handlePercentageChange = (pct) => {
    const p = parseFloat(pct) || 0;
    const next = { ...form, percentage: p };
    if (sourceInfo && form.payment_type !== 'final') {
      next.amount = Math.round(sourceInfo.total_agreement_amount * (p / 100) * 100) / 100;
    }
    setForm(next);
  };

  // When user changes payment_type
  const handlePaymentTypeChange = (pt) => {
    const next = { ...form, payment_type: pt };
    if (pt === 'final' && sourceInfo) {
      next.amount = sourceInfo.balance_remaining;
      next.percentage = sourceInfo.total_agreement_amount
        ? Math.round((sourceInfo.balance_remaining / sourceInfo.total_agreement_amount) * 10000) / 100
        : 0;
    }
    setForm(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (open === 'invoice') {
        await axios.post(`${API}/invoices`, form, { withCredentials: true });
        toast.success('Invoice created');
      } else if (open === 'bill') {
        await axios.post(`${API}/bills`, form, { withCredentials: true });
        toast.success('Bill created');
      } else if (open === 'payment') {
        await axios.post(`${API}/payments`, form, { withCredentials: true });
        toast.success('Payment recorded');
      }
      setOpen(null); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (kind, id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await axios.delete(`${API}/${kind}/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const duplicateInvoice = async (id) => {
    try {
      await axios.post(`${API}/invoices/${id}/duplicate`, {}, { withCredentials: true });
      toast.success('Invoice duplicated as draft');
      fetchAll();
    } catch (e) { toast.error('Duplicate failed'); }
  };

  const downloadInvoicePDF = (inv) => {
    const linkedPayments = payments.filter(p => p.invoice_id === inv.id);
    try {
      generateInvoicePDF({ invoice: inv, company, payments: linkedPayments });
    } catch (e) {
      toast.error('PDF generation failed');
    }
  };

  const statusColor = (s) => ({
    draft: 'bg-slate-100 text-slate-700',
    sent: 'bg-sky-100 text-sky-700',
    paid: 'bg-emerald-100 text-emerald-700',
    overdue: 'bg-rose-100 text-rose-700',
    partial: 'bg-amber-100 text-amber-700',
    unpaid: 'bg-slate-100 text-slate-700',
    cancelled: 'bg-zinc-100 text-zinc-600',
  })[s] || 'bg-slate-100 text-slate-700';

  const Tab = ({ id, label, count }) => (
    <button
      data-testid={`tab-${id}`}
      onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === id ? 'bg-amber-500 text-slate-900 shadow' : 'bg-card text-foreground hover:bg-muted border border-border'}`}
    >
      {label} {count != null && <span className="ml-1 text-xs opacity-70">({count})</span>}
    </button>
  );

  return (
    <Layout>
      <div data-testid="accounting-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <Wallet className="w-6 h-6 text-amber-600" />{t('nav.accounting')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('accounting.subtitle') === 'accounting.subtitle' ? 'Invoices, supplier bills, payments, and cash-flow health.' : t('accounting.subtitle')}</p>
          </div>
          {canWrite && (
            <div className="flex gap-2">
              <Button data-testid="new-invoice-btn" onClick={openInvoice} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <Plus className="w-4 h-4 mr-1" />{t('common.newInvoice')}
              </Button>
              <Button data-testid="new-bill-btn" onClick={openBill} variant="outline">
                <Plus className="w-4 h-4 mr-1" />{t('common.newBill')}
              </Button>
            </div>
          )}
        </div>

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label={t('accounting.ar')} value={formatCurrency(summary.accounts_receivable.total, symbol)} sub={t('accounting.invoicesCount', { count: summary.accounts_receivable.count, outstanding: formatCurrency(summary.accounts_receivable.outstanding, symbol) })} color="emerald" Icon={TrendingUp} />
            <StatCard label={t('accounting.arOverdue')} value={formatCurrency(summary.accounts_receivable.overdue, symbol)} sub={t('accounting.pastDueInvoices')} color="rose" Icon={AlertTriangle} />
            <StatCard label={t('accounting.ap')} value={formatCurrency(summary.accounts_payable.total, symbol)} sub={t('accounting.billsCount', { count: summary.accounts_payable.count, outstanding: formatCurrency(summary.accounts_payable.outstanding, symbol) })} color="amber" Icon={TrendingDown} />
            <StatCard label={t('accounting.apOverdue')} value={formatCurrency(summary.accounts_payable.overdue, symbol)} sub={t('accounting.owedPastDue')} color="slate" Icon={Receipt} />
          </div>
        )}

        {/* Filter bar: building + date range */}
        <Card className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <BuildingFilter value={buildingFilter} onChange={setBuildingFilter} buildings={buildings} />
            <div className="flex flex-col">
              <Label className="text-xs text-muted-foreground mb-1">{t('accounting.dateFrom')}</Label>
              <Input type="date" data-testid="acc-date-from" className="h-9 w-[160px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs text-muted-foreground mb-1">{t('accounting.dateTo')}</Label>
              <Input type="date" data-testid="acc-date-to" className="h-9 w-[160px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            {(buildingFilter !== 'all' || dateFrom || dateTo) && (
              <Button data-testid="acc-clear-filters" size="sm" variant="outline" onClick={() => { setBuildingFilter('all'); setDateFrom(''); setDateTo(''); }}>
                {t('accounting.clearFilters')}
              </Button>
            )}
          </div>
        </Card>

        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
          <Tab id="invoices" label={t('tab.invoices')} count={invoices.length} />
          <Tab id="bills" label={t('tab.bills')} count={bills.length} />
          <Tab id="payments" label={t('tab.payments')} count={payments.length} />
          <Tab id="aging" label={t('tab.aging')} />
        </div>

        {tab === 'invoices' && (
          <Card className="overflow-hidden">
            {loading ? <div className="py-12 text-center text-muted-foreground">{t('accounting.loading')}</div> :
             invoices.length === 0 ? <div className="py-12 text-center text-muted-foreground">{t('accounting.noInvoices')}</div> :
             <div className="overflow-x-auto">
               <table className="w-full text-sm">
                 <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground uppercase">
                   <th className="px-4 py-2">{t('th.number')}</th><th className="px-4 py-2">{t('th.customer')}</th><th className="px-4 py-2">Source</th><th className="px-4 py-2">{t('th.issue')}</th><th className="px-4 py-2">{t('th.due')}</th><th className="px-4 py-2 text-right">{t('th.total')}</th><th className="px-4 py-2 text-right">{t('th.paid')}</th><th className="px-4 py-2">{t('th.status')}</th><th className="px-4 py-2"></th>
                 </tr></thead>
                 <tbody className="divide-y divide-slate-100">
                   {invoices.map((inv, idx) => (
                     <tr key={inv.id} data-testid={`invoice-row-${idx}`} className="hover:bg-slate-50">
                       <td className="px-4 py-2 font-mono text-xs">{inv.invoice_number}</td>
                       <td className="px-4 py-2">{inv.customer_name}</td>
                       <td className="px-4 py-2 text-xs">
                         {inv.source_type && inv.source_type !== 'standalone' ? (
                           <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`invoice-source-${idx}`}>
                             <Link2 className="w-3 h-3" />
                             {inv.source_type === 'agreement' ? 'AMC' : 'LPO'} · {inv.source_ref || '—'}
                             {inv.payment_type && inv.payment_type !== 'standalone' && (
                               <span className="ml-1 capitalize">({inv.payment_type}{inv.percentage ? ` ${inv.percentage}%` : ''})</span>
                             )}
                           </span>
                         ) : <span className="text-slate-400">—</span>}
                       </td>
                       <td className="px-4 py-2">{inv.issue_date}</td>
                       <td className="px-4 py-2">{inv.due_date}</td>
                       <td className="px-4 py-2 text-right font-semibold">{formatCurrency(inv.total_amount, symbol)}</td>
                       <td className="px-4 py-2 text-right">{formatCurrency(inv.paid_amount, symbol)}</td>
                       <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${statusColor(inv.status)}`}>{(() => { const x = t(`status.${inv.status}`); return x === `status.${inv.status}` ? inv.status : x; })()}</span></td>
                       <td className="px-4 py-2 text-right">
                         <Button data-testid={`invoice-pdf-${idx}`} size="sm" variant="outline" onClick={() => downloadInvoicePDF(inv)} title={t('common.downloadPdf')}><Download className="w-3.5 h-3.5" /></Button>
                         {canWrite && <>
                           <Button data-testid={`invoice-pay-${idx}`} size="sm" variant="outline" className="ml-1" onClick={() => openPayment({ invoice_id: inv.id, amount: Math.max(inv.total_amount - inv.paid_amount, 0), direction: 'in' })}>{t('accounting.recordPayment')}</Button>
                           <Button data-testid={`invoice-duplicate-${idx}`} size="sm" variant="outline" className="ml-1" title={t('common.duplicate')} onClick={() => duplicateInvoice(inv.id)}><Copy className="w-3.5 h-3.5" /></Button>
                           <Button data-testid={`invoice-del-${idx}`} size="sm" variant="ghost" className="text-rose-600 ml-1" onClick={() => remove('invoices', inv.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                         </>}
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>}
          </Card>
        )}

        {tab === 'bills' && (
          <Card className="overflow-hidden">
            {bills.length === 0 ? <div className="py-12 text-center text-muted-foreground">{t('accounting.noBills')}</div> :
             <div className="overflow-x-auto">
               <table className="w-full text-sm">
                 <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground uppercase">
                   <th className="px-4 py-2">{t('th.number')}</th><th className="px-4 py-2">{t('th.supplier')}</th><th className="px-4 py-2">{t('th.issue')}</th><th className="px-4 py-2">{t('th.due')}</th><th className="px-4 py-2 text-right">{t('th.total')}</th><th className="px-4 py-2 text-right">{t('th.paid')}</th><th className="px-4 py-2">{t('th.status')}</th><th className="px-4 py-2"></th>
                 </tr></thead>
                 <tbody className="divide-y divide-slate-100">
                   {bills.map((b, idx) => (
                     <tr key={b.id} data-testid={`bill-row-${idx}`} className="hover:bg-slate-50">
                       <td className="px-4 py-2 font-mono text-xs">{b.bill_number}</td>
                       <td className="px-4 py-2">{b.supplier_name}</td>
                       <td className="px-4 py-2">{b.issue_date}</td>
                       <td className="px-4 py-2">{b.due_date}</td>
                       <td className="px-4 py-2 text-right font-semibold">{formatCurrency(b.total_amount, symbol)}</td>
                       <td className="px-4 py-2 text-right">{formatCurrency(b.paid_amount, symbol)}</td>
                       <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${statusColor(b.status)}`}>{(() => { const x = t(`status.${b.status}`); return x === `status.${b.status}` ? b.status : x; })()}</span></td>
                       <td className="px-4 py-2 text-right">
                         {canWrite && <>
                           <Button data-testid={`bill-pay-${idx}`} size="sm" variant="outline" onClick={() => openPayment({ bill_id: b.id, amount: Math.max(b.total_amount - b.paid_amount, 0), direction: 'out' })}>{t('accounting.recordPayment')}</Button>
                           <Button data-testid={`bill-del-${idx}`} size="sm" variant="ghost" className="text-rose-600 ml-1" onClick={() => remove('bills', b.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                         </>}
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>}
          </Card>
        )}

        {tab === 'payments' && (
          <Card className="overflow-hidden">
            {payments.length === 0 ? <div className="py-12 text-center text-muted-foreground">{t('accounting.noPayments')}</div> :
             <div className="overflow-x-auto">
               <table className="w-full text-sm">
                 <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground uppercase">
                   <th className="px-4 py-2">{t('th.date')}</th><th className="px-4 py-2">{t('th.direction')}</th><th className="px-4 py-2">{t('th.reference')}</th><th className="px-4 py-2">{t('th.method')}</th><th className="px-4 py-2 text-right">{t('th.amount')}</th><th className="px-4 py-2"></th>
                 </tr></thead>
                 <tbody className="divide-y divide-slate-100">
                   {payments.map((p, idx) => (
                     <tr key={p.id} data-testid={`payment-row-${idx}`} className="hover:bg-slate-50">
                       <td className="px-4 py-2">{p.paid_at}</td>
                       <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${p.direction === 'in' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{p.direction === 'in' ? t('status.in') : t('status.out')}</span></td>
                       <td className="px-4 py-2 text-xs font-mono">{p.invoice_id ? `Inv ${p.invoice_id.slice(0, 8)}` : p.bill_id ? `Bill ${p.bill_id.slice(0, 8)}` : '—'}</td>
                       <td className="px-4 py-2 capitalize">{(() => { const x = t(`accounting.method.${p.method}`); return x === `accounting.method.${p.method}` ? p.method : x; })()}</td>
                       <td className="px-4 py-2 text-right font-semibold">{formatCurrency(p.amount, symbol)}</td>
                       <td className="px-4 py-2 text-right">{canWrite && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove('payments', p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}</td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>}
          </Card>
        )}

        {tab === 'aging' && summary && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[[t('accounting.arAging'), summary.ar_aging], [t('accounting.apAging'), summary.ap_aging]].map(([title, buckets]) => (
              <Card key={title} className="p-4">
                <h3 className="font-semibold text-foreground mb-3">{title}</h3>
                <div className="space-y-2">
                  {[[t('accounting.aging.current'), 'current'], [t('accounting.aging.1_30'), '1_30'], [t('accounting.aging.31_60'), '31_60'], [t('accounting.aging.61_90'), '61_90'], [t('accounting.aging.over_90'), 'over_90']].map(([l, k]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{l}</span>
                      <span className="font-semibold">{formatCurrency(buckets[k], symbol)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Invoice / Bill / Payment dialog */}
        <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
          <DialogContent data-testid="accounting-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {open === 'invoice' && t('common.newInvoice')}
                {open === 'bill' && t('accounting.newSupplierBill')}
                {open === 'payment' && t('accounting.recordPaymentTitle')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {open === 'invoice' && <>
                <div><Label>{t('accounting.invoiceNumber')}</Label><Input data-testid="inv-number" value={form.invoice_number || ''} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
                <div><Label>{t('accounting.customer')}</Label><Input data-testid="inv-customer" value={form.customer_name || ''} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
                <div><Label>{t('accounting.building')}</Label>
                  <Select value={form.building_id || 'none'} onValueChange={(v) => setForm({ ...form, building_id: v === 'none' ? null : v })}>
                    <SelectTrigger data-testid="inv-building"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {buildings.map(bb => <SelectItem key={bb.id} value={bb.id}>{bb.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* ── Phase 24: Advanced Payment Options ── */}
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-800/40 p-3 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                    <Link2 className="w-4 h-4" /> Link to Agreement / LPO (optional)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Source</Label>
                      <Select value={form.source_type || 'standalone'} onValueChange={handleSourceTypeChange}>
                        <SelectTrigger data-testid="inv-source-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standalone">Standalone (no link)</SelectItem>
                          <SelectItem value="agreement">Agreement / AMC Contract</SelectItem>
                          <SelectItem value="lpo">LPO / Purchase Order</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Payment Type</Label>
                      <Select
                        value={form.payment_type || 'standalone'}
                        onValueChange={handlePaymentTypeChange}
                        disabled={!form.source_type || form.source_type === 'standalone'}
                      >
                        <SelectTrigger data-testid="inv-payment-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standalone">Standalone</SelectItem>
                          <SelectItem value="advance">Advance</SelectItem>
                          <SelectItem value="progressive">Progressive</SelectItem>
                          <SelectItem value="final">Final</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {form.source_type === 'agreement' && (
                    <div>
                      <Label>Agreement</Label>
                      <Select value={form.source_id || ''} onValueChange={handleSourcePick}>
                        <SelectTrigger data-testid="inv-source-agreement"><SelectValue placeholder="Select agreement…" /></SelectTrigger>
                        <SelectContent>
                          {(invoiceSources.agreements || []).map(a => (
                            <SelectItem key={a.id} value={a.id}>
                              {(a.contract_no || a.name)} — {formatCurrency(a.contract_value || 0, symbol)}
                            </SelectItem>
                          ))}
                          {(invoiceSources.agreements || []).length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">No agreements available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {form.source_type === 'lpo' && (
                    <div>
                      <Label>LPO / Purchase Order</Label>
                      <Select value={form.source_id || ''} onValueChange={handleSourcePick}>
                        <SelectTrigger data-testid="inv-source-lpo"><SelectValue placeholder="Select LPO…" /></SelectTrigger>
                        <SelectContent>
                          {(invoiceSources.lpos || []).map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.po_number} — {p.supplier_name} — {formatCurrency(p.total_amount || 0, symbol)}
                            </SelectItem>
                          ))}
                          {(invoiceSources.lpos || []).length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">No LPOs available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {sourceInfo && (
                    <div data-testid="inv-source-info" className="rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs space-y-1">
                      <div className="flex justify-between"><span className="text-muted-foreground">Total Value</span><span className="font-semibold">{formatCurrency(sourceInfo.total_agreement_amount, symbol)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Previously Invoiced</span><span className="font-semibold text-sky-700 dark:text-sky-400">{formatCurrency(sourceInfo.previously_invoiced, symbol)}</span></div>
                      <div className="flex justify-between border-t border-slate-100 dark:border-slate-700 pt-1 mt-1"><span className="text-muted-foreground">Balance Remaining</span><span className="font-bold text-emerald-700 dark:text-emerald-400">{formatCurrency(sourceInfo.balance_remaining, symbol)}</span></div>
                      {(sourceInfo.prior_invoices || []).length > 0 && (
                        <div className="pt-2 mt-1 border-t border-slate-100 dark:border-slate-700">
                          <div className="text-slate-500 mb-1">Payment Schedule:</div>
                          <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                            {sourceInfo.prior_invoices.map(pi => (
                              <li key={pi.id} className="flex justify-between font-mono text-[11px]">
                                <span>{pi.invoice_number} ({pi.payment_type || 'std'} {pi.percentage ? `· ${pi.percentage}%` : ''})</span>
                                <span>{formatCurrency(pi.total_amount, symbol)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {loadingSource && <div className="text-xs text-muted-foreground">Loading balance…</div>}

                  {form.source_type !== 'standalone' && form.source_id && form.payment_type !== 'final' && form.payment_type !== 'standalone' && (
                    <div>
                      <Label>Percentage of Total (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        data-testid="inv-percentage"
                        value={form.percentage || ''}
                        onChange={(e) => handlePercentageChange(e.target.value)}
                        placeholder="e.g. 25"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Amount will auto-update as you type.</div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('accounting.issueDate')}</Label><Input type="date" value={form.issue_date || ''} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
                  <div><Label>{t('accounting.dueDate')}</Label><Input type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('accounting.amount')}</Label><Input type="number" data-testid="inv-amount" value={form.amount ?? ''} disabled={form.payment_type === 'final'} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>{t('accounting.tax')}</Label><Input type="number" value={form.tax_amount ?? ''} onChange={(e) => setForm({ ...form, tax_amount: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div><Label>{t('accounting.description')}</Label><Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </>}
              {open === 'bill' && <>
                <div><Label>{t('accounting.billNumber')}</Label><Input data-testid="bill-number" value={form.bill_number || ''} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} /></div>
                <div><Label>{t('accounting.supplier')}</Label><Input data-testid="bill-supplier" value={form.supplier_name || ''} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} /></div>
                <div><Label>{t('accounting.building')}</Label>
                  <Select value={form.building_id || 'none'} onValueChange={(v) => setForm({ ...form, building_id: v === 'none' ? null : v })}>
                    <SelectTrigger data-testid="bill-building"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {buildings.map(bb => <SelectItem key={bb.id} value={bb.id}>{bb.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('accounting.issueDate')}</Label><Input type="date" value={form.issue_date || ''} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
                  <div><Label>{t('accounting.dueDate')}</Label><Input type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('accounting.amount')}</Label><Input type="number" data-testid="bill-amount" value={form.amount ?? ''} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>{t('accounting.tax')}</Label><Input type="number" value={form.tax_amount ?? ''} onChange={(e) => setForm({ ...form, tax_amount: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div><Label>{t('accounting.description')}</Label><Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </>}
              {open === 'payment' && <>
                <div><Label>{t('th.date')}</Label><Input type="date" value={form.paid_at || ''} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('accounting.amount')}</Label><Input type="number" data-testid="pay-amount" value={form.amount ?? ''} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>{t('accounting.method')}</Label>
                    <Select value={form.method || 'bank'} onValueChange={(v) => setForm({ ...form, method: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank">{t('accounting.method.bank')}</SelectItem>
                        <SelectItem value="cash">{t('accounting.method.cash')}</SelectItem>
                        <SelectItem value="card">{t('accounting.method.card')}</SelectItem>
                        <SelectItem value="cheque">{t('accounting.method.cheque')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>{t('accounting.reference')}</Label><Input value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
                <div className="text-xs text-muted-foreground">{t('accounting.directionLabel')}: <strong>{form.direction === 'in' ? t('accounting.directionIn') : t('accounting.directionOut')}</strong></div>
              </>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(null)}>{t('common.cancel')}</Button>
              <Button data-testid="accounting-save-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {saving ? t('hr.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
